import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import Seo from '../common/Seo';

const Login: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  // URL-Parameter verarbeiten für Verifizierungsstatus
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('verified') === 'true') {
      setSuccess('Ihre E-Mail-Adresse wurde erfolgreich bestätigt. Sie können sich jetzt anmelden.');
    }
    
    const verificationError = params.get('verification_error');
    if (verificationError) {
      setError(`Verifizierungsfehler: ${verificationError}`);
    }
  }, [location]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    try {
      const response = await login(email, password);
      
      if (!response.success) {
        setError(response.message || 'Login fehlgeschlagen');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Ein Fehler ist aufgetreten');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col items-center pt-16">
      <Seo title="Login | SportyReplay" path="/login" noindex />

      <div className="text-3xl font-bold mb-8">
        <span className="text-white">Sporty</span>
        <span className="text-orange-500">Replay</span>
      </div>
      
      <div className="w-full max-w-md bg-white rounded-lg shadow-xl p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Anmelden</h2>
        
        {/* Erfolgsmeldung anzeigen */}
        {success && (
          <div className="mb-4 p-2 bg-green-100 border border-green-400 text-green-700 rounded">
            {success}
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="email">
              E-Mail
            </label>
            <input
              type="email"
              id="email"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="password">
              Passwort
            </label>
            <input
              type="password"
              id="password"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          
          {/* Passwort vergessen Link */}
          <div className="mb-4 flex justify-end">
            <Link 
              to="/forgot-password" 
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Passwort vergessen?
            </Link>
          </div>
          
          <button
            type="submit"
            className="w-full bg-orange-500 text-white py-3 px-4 rounded hover:bg-orange-600 transition duration-200"
          >
            Anmelden
          </button>
        </form>

        <p className="mt-4 text-center text-gray-600">
          Noch kein Konto?{' '}
          <Link to="/register" className="text-orange-500 hover:text-orange-700">
            Registrieren
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;